import { useEffect, useState } from "react";
import { ConfirmDialog } from "./ConfirmDialog";

/** "Are you sure?", asked on the page itself. The browser's own pop-up (window.confirm) is answered "no"
 * without ever being shown by some browsers - the one built into the Claude app among them - so a button
 * guarded by it silently did nothing. Await `askToConfirm(...)` and go ahead on true; `<ConfirmHost />`,
 * mounted once beside `<Toaster />`, shows the question. Never use window.confirm in this app. */

interface Ask {
  question: string;
  title: string;
  yes: string;
  danger: boolean;
  resolve: (ok: boolean) => void;
}

const TARGET = "proofbox-confirm";
let hosts = 0;

export function askToConfirm(
  question: string,
  opts: { title?: string; yes?: string; danger?: boolean } = {},
): Promise<boolean> {
  // A page outside the app's frame has no host: the browser's pop-up then is better than a button that waits for ever.
  if (hosts === 0) return Promise.resolve(window.confirm(question));
  return new Promise((resolve) => {
    const ask: Ask = { question, title: opts.title ?? "Are you sure?", yes: opts.yes ?? "Yes", danger: !!opts.danger, resolve };
    window.dispatchEvent(new CustomEvent(TARGET, { detail: ask }));
  });
}

export function ConfirmHost() {
  const [ask, setAsk] = useState<Ask | null>(null);
  useEffect(() => {
    hosts += 1;
    const onAsk = (e: Event) => {
      const next = (e as CustomEvent).detail as Ask;
      setAsk((cur) => {
        if (cur) cur.resolve(false);   // a new question replaces one left unanswered
        return next;
      });
    };
    window.addEventListener(TARGET, onAsk);
    return () => {
      hosts -= 1;
      window.removeEventListener(TARGET, onAsk);
    };
  }, []);
  if (!ask) return null;
  const answer = (ok: boolean) => {
    ask.resolve(ok);
    setAsk(null);
  };
  return (
    <ConfirmDialog
      open
      title={ask.title}
      body={<p>{ask.question}</p>}
      confirmLabel={ask.yes}
      danger={ask.danger}
      onConfirm={() => answer(true)}
      onCancel={() => answer(false)}
    />
  );
}
